import GridContainer from "@/components/common/GridContainer";
import {capitalize, getCssSizeValue, getCurrentYear, getMonthNames} from "@/utils/utils";
import styled from "styled-components";
import {useRouter} from "next/router";
import SingleSelect from "@/components/selectors/SingleSelect";
import {DROPDOWN_VARIANTS} from "@/components/common/Dropdown";
import React, {useEffect, useMemo, useState} from "react";
import {LOCALES, PAGES} from "@/constants";
import {BREAKPOINTS} from "@/styles/themeSettings";
import {useBranch} from "@/hooks/useBranch";
import MultiSelect from "@/components/selectors/MultiSelect";
import FiltersReset from "@/components/articlesWithPagination/FiltersReset";
import Container from "@/components/common/Container";

const StyledWrapper = styled(GridContainer)`
    column-gap: ${getCssSizeValue(28, BREAKPOINTS.MOBILE)};

    @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
        column-gap: var(--grid-gap);
        margin-bottom: ${getCssSizeValue(54, BREAKPOINTS.DESKTOP)};
    }
`

const InputWrapper = styled.div`
    height: 55px;
    width: 100%;

    .multiselect {
        width: 100%;
    }

    .select {
        width: 100%;
    }

    @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
        .multiselect {
        }
    }
`

const StyledSelectedFilters = styled.div`
	margin-top: ${getCssSizeValue(22, BREAKPOINTS.MOBILE)};
	display: flex;
	align-items: flex-start;
	width: 100%;

    @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
		margin-top: unset;
		display: initial;
	}

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
    	display: block;
        padding: 0 var(--paddingX);
	}
	
	.selected-filters {
		display: flex;
		align-items: flex-start;
		flex-direction: column;
		text-transform: uppercase;
		color: ${({theme}) => theme.colors.richBlack};
		font-weight: ${({theme}) => theme.fontWeight.medium};
		line-height: normal !important;

        gap: ${getCssSizeValue(6, BREAKPOINTS.MOBILE)};
		
		@media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
			gap: ${getCssSizeValue(12, BREAKPOINTS.TABLET)};
		}
	
		@media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			flex-direction: row;
			margin: unset;
			margin-bottom: ${getCssSizeValue(50, BREAKPOINTS.DESKTOP)};
		}
	}
`

const SelectedOptionWrapper = styled.div`
	position: relative;
	width: 65%;

	margin-left: calc(var(--paddingX) + 5px);
	
    @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
		width: initial;
        margin-left: calc(var(--paddingX) + ${getCssSizeValue(10, BREAKPOINTS.TABLET)});
	}
    
	@media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
	    margin-left: ${getCssSizeValue(18)};
    }
`

const SelectedOption = styled.div`
	cursor: pointer;
    &:before {
		--size: ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};
        content: '';
        background-color: ${({theme}) => theme.colors.richBlack};
        position: absolute;
        top: ${getCssSizeValue(9, BREAKPOINTS.TABLET)};
        left: ${getCssSizeValue(-18)};
        width: var(--size);
        height: var(--size);

        @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
            top: ${getCssSizeValue(6, BREAKPOINTS.TABLET)};
            --size: ${getCssSizeValue(8, BREAKPOINTS.TABLET)};
		}

        @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
            top: ${getCssSizeValue(6)};
            --size: ${getCssSizeValue(8, BREAKPOINTS.DESKTOP)};
		}
    }
`

export const CATEGORIES = {
	announcement: 'Ogłoszenie',
	tender: 'Przetarg',
	space_rental: 'Wynajem powierzchni'
}

function mapAnnouncementsYearsToSelectValues(options) {
	return options.map(option => ({
		id: option?.id || '',
		year: option?.slug || getCurrentYear(),
		label: option?.slug || getCurrentYear(),
		value: option?.slug || getCurrentYear()
	}))
}

function mapMonthsToSelectValues(values, data = []) {
	const enLocaleMonths = getMonthNames(LOCALES.EN);

	return values.map((value, index) => {
		const amount = data.filter((item) => {
			const month = item?.month?.month || null
			return month === enLocaleMonths[index].toLowerCase()
		}).length;

		return {
			id: value,
			value: enLocaleMonths[index].toLowerCase(),
			label: capitalize(value),
			amount,
			disabled: false,
		}
	})
}

export function getMappedCategoriesToSelectValues(data = []) {
	const categoriesList = Object.entries(CATEGORIES)
	return categoriesList.map(([key, value]) => {
		const amount = data.filter(item => {
			return item.pageType === key
		}).length;

		return {
			id: key,
			value: key.toLowerCase(),
			label: value,
			amount,
			disabled: false,
		}
	})
}

export function getPathnameYear(router) {
	return router.asPath.split("/").find((segment) => /\d/.test(segment));
}

function getInitialSelectedYear(yearSelectOptions, pathnameYear) {
	return yearSelectOptions.find(option => Number(option.value) === Number(pathnameYear)) || yearSelectOptions[0];
}

export default function AnnouncementFiltration({announcementsYears = [], initialData, setFilteredData}) {
	const router = useRouter()
	const yearSelectOptions = mapAnnouncementsYearsToSelectValues(announcementsYears)
	const pathnameYear = getPathnameYear(router);
	const initialSelectedYear = getInitialSelectedYear(yearSelectOptions, pathnameYear);
	const [selectedYear, setSelectedYear] = useState(initialSelectedYear);

	const {
		dropdownMonths,
		dropdownCategories,
		handleMonthChange,
		selectedMonthsFromQuery,
		selectedCategoriesFromQuery,
		setSelectedMonth,
		handleCategoryChange,
		setSelectedCategory,
		handleRemoveSingleFilter
	} = useFilterAnnouncements(initialData, setFilteredData)

	const handleYearChange = ({target}) => {
		const {value} = target;
		const selectedValue = yearSelectOptions.find(year => Number(year.value) === Number(value))
		setSelectedYear(selectedValue)
		setSelectedMonth(null)
		setSelectedCategory(null)
		router.push(`/${PAGES.OTHERS}/${PAGES.ANNOUNCEMENTS_AND_TENDERS}/${value}`)
	}

	const selectedMonths = dropdownMonths.filter(month => selectedMonthsFromQuery.includes(month.value))
	const selectedCategories = dropdownCategories.filter(category => selectedCategoriesFromQuery.includes(category.value))

	return (
		<>
			<StyledWrapper>
				<InputWrapper className='col-mobile-4 col-desktop-3'>
					<SingleSelect
						name='year-select'
						options={yearSelectOptions}
						selectedValue={selectedYear}
						onChange={handleYearChange}
						variant={DROPDOWN_VARIANTS.TERTIARY}
					/>
				</InputWrapper>
				<InputWrapper className='col-mobile-4 col-desktop-3'>
					<MultiSelect
						displaySelectedOptions={false}
						name='month-select'
						options={dropdownMonths}
						selectedValues={selectedMonths}
						placeholder={translations[router.locale]?.monthSelectPlaceholder || 'Month'}
						onChange={handleMonthChange}
						variant={DROPDOWN_VARIANTS.TERTIARY}
					/>
				</InputWrapper>
				<InputWrapper className='col-mobile-8 col-desktop-3 w-full'>
					<MultiSelect
						displaySelectedOptions={false}
						name='category-select'
						options={dropdownCategories}
						selectedValues={selectedCategories}
						placeholder={translations[router.locale]?.categorySelectPlaceholder || 'Category'}
						onChange={handleCategoryChange}
						variant={DROPDOWN_VARIANTS.TERTIARY}
					/>
				</InputWrapper>
			</StyledWrapper>
			<StyledSelectedFilters className='typo-strawford typo-strawford-35'>
				<div className='selected-filters'>
					{selectedMonths.length !== 0 && (
						<SelectedOptionWrapper>
							<SelectedOption>
								{
									selectedMonths.map((item, i) => {
										return (
											<span key={item?.id || i} onClick={() => handleRemoveSingleFilter('months', item.value)}>
												{item.label}
												{i !== selectedMonths.length - 1 ? <span>,&nbsp; </span> : null}
											</span>
										)
									})
								}
							</SelectedOption>
						</SelectedOptionWrapper>
					)}
					{selectedCategories.length !== 0 && (
						<SelectedOptionWrapper>
							<SelectedOption>
								{
									selectedCategories.map((item, i) => {
										return (
											<span key={item?.id || i} onClick={() => handleRemoveSingleFilter('categories', item.value)}>
												{item.label}
												{i !== selectedCategories.length - 1 ? <span>,&nbsp; </span> : null}
											</span>
										)
									})
								}
							</SelectedOption>
						</SelectedOptionWrapper>
					)}
				</div>
			</StyledSelectedFilters>
		</>
	)
}

function useFilterAnnouncements(data, setFilteredData) {
	const {selectedOption} = useBranch()
	const router = useRouter();
	const {query} = router;
	const [selectedMonth, setSelectedMonth] = useState(null)
	const [selectedCategory, setSelectedCategory] = useState(null)

	const dropdownMonths = useMemo(() => {
		const monthNames = getMonthNames(router.locale);
		return mapMonthsToSelectValues(monthNames, data)
	}, [data, router])

	const dropdownCategories = useMemo(() => getMappedCategoriesToSelectValues(data), [data]);

	const selectedMonthsFromQuery = useMemo(() => {
		if (!query.months) return [];
		return query.months.split('_');
	}, [query.months]);

	const selectedCategoriesFromQuery = useMemo(() => {
		if (!query.categories) return [];
		return query.categories.split('_');
	}, [query.categories])


	useEffect(() => {
		let result = data;

		if (selectedMonthsFromQuery.length > 0) {
			result = data.filter(item => {
				const month = item?.month?.month || null
				if (month) {
					return selectedMonthsFromQuery.includes(month)
				}
			})
		}

		if (selectedCategoriesFromQuery.length > 0) {

			result = result.filter(item => {
				const pageType = item?.pageType || null

				if (pageType) {
					return selectedCategoriesFromQuery.includes(pageType)
				}
			})
		}

		setFilteredData(result)
	}, [selectedMonthsFromQuery, selectedCategoriesFromQuery, router.asPath, selectedOption]);

	const handleMonthChange = (selected) => {
		const selectedMonths = selected.target.value.map(month => month.value).join('_');

		const updatedQuery = {...query};

		if (selectedMonths) {
			updatedQuery.months = selectedMonths;
		} else {
			delete updatedQuery.months;
		}

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, {shallow: true});
	};

	const handleCategoryChange = (selected) => {
		const selectedCategories = selected.target.value.map(category => category.value).join('_');

		const updatedQuery = {...query};

		if (selectedCategories) {
			updatedQuery.categories = selectedCategories;
		} else {
			delete updatedQuery.categories;
		}

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, {shallow: true});
	};

	const handleResetFiltration = () => {
		router.replace({
			pathname: router.pathname,
			query: {
				branch: selectedOption
			}
		}, undefined, {shallow: true})
	}

	const handleRemoveSingleFilter = (filterName, filterValue) => {
		const updatedQuery = { ...query };

		if (updatedQuery[filterName]) {
			const filters = updatedQuery[filterName]?.split('_')?.filter(filter => filter !== filterValue);
			if (filters.length) {
				updatedQuery[filterName] = filters.join('_');
			} else {
				delete updatedQuery[filterName];
			}
		}

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, { shallow: true });
	};

	return {
		dropdownMonths,
		dropdownCategories,
		handleMonthChange,
		selectedMonthsFromQuery,
		selectedCategoriesFromQuery,
		handleCategoryChange,
		setSelectedCategory,
		handleResetFiltration,
		setSelectedMonth,
		handleRemoveSingleFilter
	};
}

const translations = {
	pl: {
		monthSelectPlaceholder: 'Wybierz Miesiąc',
		categorySelectPlaceholder: 'Wybierz Kategorię'
	},
	en: {
		monthSelectPlaceholder: 'Select Month',
		categorySelectPlaceholder: 'Select Category'
	},
	de: {
		monthSelectPlaceholder: 'Monat Auswählen',
		categorySelectPlaceholder: 'Kategorien Auswählen'
	}
}